import * as React from "react"
import { InputBaseProps } from "@repo/design-system/types"
import { cn } from "@/lib/utils"

export interface WebInputProps extends InputBaseProps {
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
}

export const inputVariants = {
  base: "flex h-10 w-full premium-radius-md border border-input bg-transparent text-foreground px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  variant: {
    default: "border-input",
    filled: "bg-muted border-transparent",
    outlined: "border-2",
    unstyled: "border-none shadow-none",
    ghost: "border-none bg-transparent shadow-none",
    error: "border-red-500 focus-visible:ring-red-500",
    success: "border-green-500 focus-visible:ring-green-500",
    glass: "bg-white/10 backdrop-blur-sm border-transparent",
  },
  size: {
    sm: "h-8 px-2 text-xs",
    md: "h-10 px-3 text-sm",
    lg: "h-12 px-4 text-base",
    default: "h-10 px-3 text-sm",
    xl: "h-14 px-5 text-lg",
  },
};

export const Input = React.forwardRef<HTMLInputElement, WebInputProps>(
  ({
    className,
    variant = "default",
    inputSize = "default",
    type = "text",
    state,
    label,
    helperText,
    error,
    disabled,
    value,
    defaultValue,
    onValueChange,
    onChange,
    required,
    leftIcon,
    rightIcon,
    id,
    radius,
    ...props
  }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      if (onValueChange) {
        onValueChange(e.target.value);
      }
    };

    const variantClass = inputVariants.variant[variant as keyof typeof inputVariants.variant] || inputVariants.variant.default;
    const sizeClass = inputVariants.size[inputSize as keyof typeof inputVariants.size] || inputVariants.size.default;
    const radiusClass = radius ? `premium-radius-${radius}` : "premium-radius-input";

    return (
      <div className="relative w-full" suppressHydrationWarning>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative" suppressHydrationWarning>
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input
            type={type}
            autoComplete="new-password" 
            data-lpignore="true" 
            data-1p-ignore="true" 
            data-form-type="other" 
            data-no-autofill="true" 
            data-lastpass-ignore="true"
            data-bitwarden-ignore="true"
            autoCorrect="off" 
            autoCapitalize="off" 
            spellCheck="false"
            className={cn(
              inputVariants.base,
              variantClass,
              sizeClass,
              leftIcon && "pl-10",
              rightIcon && "pr-10",
              error && inputVariants.variant.error,
              state === "error" && inputVariants.variant.error,
              state === "success" && inputVariants.variant.success,
              radiusClass,
              className
            )}
            ref={ref}
            disabled={disabled}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            required={required}
            id={id}
            suppressHydrationWarning
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>
        {(helperText || error) && (
          <p className={cn(
            "mt-1 text-sm",
            error ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          )}>
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"
